import React from 'react';
import {
    CheckCircle,
    ArrowRightCircle,
    Star,
} from 'lucide-react';
import {
    InformationCircleIcon,
    BoltIcon,
    AcademicCapIcon,
} from '@heroicons/react/24/outline';

/* ------------------------------------------------------------------
 * Types
 * ---------------------------------------------------------------- */

export type FeatureListIcon =
    | 'check'
    | 'arrow'
    | 'star'
    | 'info'
    | 'bolt'
    | 'cap';

export type FeatureListItem = {
    title?: string;
    description?: string; // HTML-safe string
};

export type FeatureListStyle =
    | 'number'
    | 'icon'
    | 'number-icon';

export type FeatureListVariant =
    | 'plain'
    | 'bordered'
    | 'soft';

export type FeatureListProps = {
    title?: string;
    items: FeatureListItem[];
    icon?: FeatureListIcon;
    style?: FeatureListStyle;
    variant?: FeatureListVariant;
    dark?: boolean;
};

/* ------------------------------------------------------------------
 * Icon whitelist
 * ---------------------------------------------------------------- */

const ICON_MAP: Record<FeatureListIcon, React.ElementType> = {
    check: CheckCircle,
    arrow: ArrowRightCircle,
    star: Star,
    info: InformationCircleIcon,
    bolt: BoltIcon,
    cap: AcademicCapIcon,
};

/* ------------------------------------------------------------------
 * Style tokens
 * ---------------------------------------------------------------- */

const ITEM_VARIANT_TOKENS: Record<
    FeatureListVariant,
    { light: string; dark: string }
> = {
    plain: {
        light: '',
        dark: '',
    },
    bordered: {
        light: 'border border-gray-200',
        dark: 'border border-gray-800',
    },
    soft: {
        light: 'bg-gray-50',
        dark: 'bg-gray-900/60',
    },
};

const BADGE_TOKENS = {
    light: 'bg-white text-black',
    dark: 'bg-gray-800 text-gray-200',
};

const TEXT_TOKENS = {
    light: {
        title: 'text-black',
        body: 'text-black',
    },
    dark: {
        title: 'text-white',
        body: 'text-gray-300',
    },
};

/* ------------------------------------------------------------------
 * Component
 * ---------------------------------------------------------------- */

const FeaturePoints = ({
                           title,
                           items,
                           icon = 'check',
                           style = 'number-icon',
                           variant = 'bordered',
                           dark,
                       }: FeatureListProps) => {
    if (!items?.length) return null;

    const Icon = ICON_MAP[icon];
    const text = dark ? TEXT_TOKENS.dark : TEXT_TOKENS.light;
    const badgeClass = dark ? BADGE_TOKENS.dark : BADGE_TOKENS.light;
    const itemVariantClass = dark
        ? ITEM_VARIANT_TOKENS[variant].dark
        : ITEM_VARIANT_TOKENS[variant].light;

    return (
        <section className="space-y-5">
            {!!title && (
                <h3 className={`text-xl font-semibold ${text.title}`}>
                    {title}
                </h3>
            )}

            <ol className="space-y-4">
                {items.map((item, index) => (
                    <li
                        key={`${item.title}-${index}`}
                        className={`relative flex gap-4 rounded-xl p-4 ${itemVariantClass}`}
                    >
                        {/* Icon badge */}
                        {(style === 'icon' || style === 'number-icon') && (
                            <div
                                className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full ${badgeClass}`}
                            >
                                <span className="sr-only">
                                    Feature {index + 1}
                                </span>
                                <Icon className="h-5 w-5" />
                            </div>
                        )}

                        {/* Content */}
                        <div className="flex-1">
                            <div className="flex items-center gap-2">
                                {(style === 'number' ||
                                    style === 'number-icon') && (
                                    <span className={`text-sm font-semibold ${text.title}`}>
                                        {index + 1}.
                                    </span>
                                )}

                                {!!item.title && (
                                    <h4 className={`!text-sm !font-medium ${text.title}`}>
                                        {item.title}
                                    </h4>
                                )}
                            </div>

                            {!!item.description && (
                                <div
                                    className={`prose prose-sm mt-1 !text-sm max-w-none ${text.body}`}
                                    dangerouslySetInnerHTML={{
                                        __html: item.description,
                                    }}
                                />
                            )}
                        </div>
                    </li>
                ))}
            </ol>
        </section>
    );
};

export default FeaturePoints;
